import { Controller } from "@hotwired/stimulus"

// Houdt de invoer van een formulier vast in de browser, zodat een sessie die
// verloopt geen half uur werk kost. De sessie blijft 12 uur; wie 's ochtends
// inlogt en 's avonds zijn uren invult komt op het inlogscherm uit, en zonder dit
// is alles wat hij intikte weg.
//
// De sleutel draagt het gebruikersnummer, want een telefoon wordt gedeeld: de
// invoer van de een mag nooit bij de ander terugkomen. Concepten ouder dan een
// week worden opgeruimd, van wie ze ook zijn.
//
// Bestanden (een foto) gaan niet mee; die kan de browser niet teruggeven.
export default class extends Controller {
  static targets = ["notice", "adder"]
  static values = { key: String, maxAge: { type: Number, default: 7 * 24 * 60 * 60 * 1000 } }

  static PREFIX = "gardentime:draft:"
  static SKIP_NAMES = ["authenticity_token", "_method", "utf8", "return_to"]
  static MAX_ROWS = 30

  connect() {
    this.sweep()
    // De controllers die tijdvakken en materiaalregels bijplaatsen zitten in dit
    // formulier en starten dus later. Eén frame wachten scheelt een rij die
    // nergens heen kan.
    requestAnimationFrame(() => this.restore())
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  // Elke toetsaanslag wegschrijven is zonde; een halve seconde stilte is genoeg.
  // Tijdens het terugzetten niets wegschrijven: het invullen stuurt zelf change,
  // en dat overschreef het concept met het half teruggezette formulier.
  save() {
    if (this.restoring) return

    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.write(), 500)
  }

  // Alleen wissen als het opslaan echt lukte. Komt het formulier terug met een
  // fout, dan staat de invoer er nog en moet het concept blijven: de sessie kan
  // ook verlopen terwijl iemand die fout aan het herstellen is.
  clear(event) {
    if (event?.detail && event.detail.success === false) return

    clearTimeout(this.timer)
    this.forget(this.storageKey)
  }

  // "Toch leegmaken": het concept weg en de pagina opnieuw, zodat precies staat
  // wat de server stuurde.
  discard() {
    this.clear()
    window.location.reload()
  }

  write() {
    const draft = { savedAt: Date.now(), fields: this.collect() }
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(draft))
    } catch (_error) {
      /* geen opslag beschikbaar: dan werkt het formulier gewoon zonder concept */
    }
  }

  restore() {
    const draft = this.read()
    if (!draft || !draft.fields?.length) return

    this.restoring = true
    try {
      this.addMissingRows(draft.fields)
      if (this.apply(draft.fields)) this.showNotice()
      // Het gewerkte totaal luistert naar dit signaal en niet naar change, dus
      // zonder deze regel stond er na het terugzetten alleen het eerste tijdvak.
      // Het moet uit het tijdvakkenblok zelf komen: de teller staat erboven en
      // een gebeurtenis borrelt alleen omhoog.
      this.element.querySelectorAll("[data-controller~='time-blocks']").forEach((blocks) => {
        blocks.dispatchEvent(new Event("time-blocks:changed", { bubbles: true }))
      })
    } finally {
      this.restoring = false
    }
  }

  collect() {
    return this.fields().map((field) => ({
      name: field.name,
      value: this.checkable(field) ? field.checked : field.value
    }))
  }

  apply(fields) {
    const waiting = new Map()
    this.positionalNames(fields.map((entry) => entry.name)).forEach((key, index) => {
      if (!waiting.has(key)) waiting.set(key, [])
      waiting.get(key).push(fields[index])
    })

    const domFields = this.fields()
    const domKeys = this.positionalNames(domFields.map((field) => field.name))
    let restored = false

    domFields.forEach((field, position) => {
      const saved = waiting.get(domKeys[position])?.shift()
      if (!saved) return

      if (this.checkable(field)) {
        if (field.checked !== saved.value) restored = true
        field.checked = saved.value
      } else {
        if (field.value !== saved.value) restored = true
        field.value = saved.value
      }
      field.dispatchEvent(new Event("change", { bubbles: true }))
      if (!this.checkable(field) && saved.value) this.reveal(field)
    })

    return restored
  }

  // Foto, materialen en opmerkingen zitten achter "+ Foto, materialen of
  // opmerkingen". Teruggezette invoer die daar in valt zou anders onzichtbaar
  // terugkomen, en dat leest als weg.
  reveal(field) {
    const section = field.closest("[data-controller~='disclosure']")
    const content = section?.querySelector("[data-disclosure-target='content']")
    if (!content?.hidden) return

    const toggle = section.querySelector("[data-disclosure-target='toggle']")
    toggle?.click()
    // Draait de controller van dat blok nog niet, dan doet die klik niets en
    // komt de invoer onzichtbaar terug. Dit is het vangnet daarvoor.
    if (content.hidden) {
      content.hidden = false
      if (toggle) toggle.hidden = true
    }
  }

  // Een bijgeplaatst tijdvak krijgt zijn nummer van de klok, dus de veldnaam van
  // nu is nooit die van gisteren. Daarom heet de eerste rij hier gewoon de eerste.
  positionalNames(names) {
    const collections = new Map()

    return names.map((name) => {
      const match = name.match(/\[([a-z_]+_attributes)\]\[([^\]]+)\]/)
      if (!match) return name

      const [ , collection, index ] = match
      if (!collections.has(collection)) collections.set(collection, new Map())

      const rows = collections.get(collection)
      if (!rows.has(index)) rows.set(index, rows.size)
      return name.replace(`[${collection}][${index}]`, `[${collection}][#${rows.get(index)}]`)
    })
  }

  // Tijdblokken en materiaalregels worden door een andere controller bijgeplaatst.
  // Zonder die rijen heeft het concept niets om in terug te zetten, dus drukt dit
  // net zo vaak op "toevoegen" als er rijen in het concept staan.
  addMissingRows(fields) {
    for (const adder of this.adderTargets) {
      const collection = adder.dataset.formDraftCollection
      if (!collection) continue

      const wanted = this.rowCount(fields.map((field) => field.name), collection)
      let guard = 0
      while (this.rowCount(this.fields().map((field) => field.name), collection) < wanted && guard < this.constructor.MAX_ROWS) {
        adder.click()
        guard += 1
      }
    }
  }

  rowCount(names, collection) {
    const pattern = new RegExp(`\\[${collection}\\]\\[([^\\]]+)\\]`)
    const rows = new Set()
    for (const name of names) {
      const match = name.match(pattern)
      if (match) rows.add(match[1])
    }
    return rows.size
  }

  showNotice() {
    if (this.hasNoticeTarget) this.noticeTarget.hidden = false
  }

  read() {
    try {
      const raw = localStorage.getItem(this.storageKey)
      if (!raw) return null

      const draft = JSON.parse(raw)
      if (this.expired(draft)) {
        this.forget(this.storageKey)
        return null
      }
      return draft
    } catch (_error) {
      return null
    }
  }

  // Ook de concepten van iemand anders op dezelfde telefoon, want die blijven
  // anders liggen tot het toestel wordt opgeruimd.
  sweep() {
    try {
      for (const key of Object.keys(localStorage)) {
        if (!key.startsWith(this.constructor.PREFIX)) continue

        const draft = JSON.parse(localStorage.getItem(key) || "null")
        if (!draft || this.expired(draft)) this.forget(key)
      }
    } catch (_error) {
      /* geen opslag beschikbaar */
    }
  }

  expired(draft) {
    return !draft.savedAt || Date.now() - draft.savedAt > this.maxAgeValue
  }

  forget(key) {
    try { localStorage.removeItem(key) } catch (_error) { /* niets om te vergeten */ }
  }

  fields() {
    return Array.from(this.element.elements).filter((field) => {
      if (!field.name || field.disabled) return false
      if (this.constructor.SKIP_NAMES.includes(field.name)) return false
      return ["text", "textarea", "date", "time", "number", "email", "tel", "url", "select-one", "select-multiple", "checkbox", "radio", "hidden", "search"].includes(field.type)
    })
  }

  checkable(field) {
    return field.type === "checkbox" || field.type === "radio"
  }

  get storageKey() {
    return `${this.constructor.PREFIX}${this.keyValue}`
  }
}
